/**
 * Auth Slice
 * Redux slice for authentication state management
 */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  AuthState,
  LoginResponse,
  WBADetailsResponse,
  WBAPhoneNumber,
  UserBusiness,
  SelectedOrganization,
  OrganizationProject,
  SelectedProject,
  PendingAuthData,
  LoginUserDetails,
  ProjectTokenClaims,
  RuntimeRolePermissionsData,
  RuntimeRolePermissionsState,
} from "./types";
import type { CurrentSubscriptionData } from "@/api/subscription/subscription.types";

export type { AuthState };

/**
 * Initial state values - extracted for reuse in reset operations
 */
const getInitialAuthState = (): AuthState => ({
  // Existing fields
  emailRefId: null,
  phoneRefId: null,

  isAuthenticated: false,
  loginResponse: null,
  accessToken: null,
  wbaDetails: null,
  selectedWBAPhoneNumber: null,

  // Pending auth data (before org/project selection)
  pendingAuth: null,

  // Project-level tokens
  projectAccessToken: null,
  projectRefreshToken: null,

  // Decoded project token claims
  projectTokenClaims: null,

  rolePermissions: getInitialRolePermissionsState(),
  currentSubscription: null,

  // Organization fields
  organizations: [],
  selectedOrganization: null,
  organizationsLoading: false,
  organizationsError: null,

  // Project fields
  projects: [],
  selectedProject: null,
  projectsLoading: false,
  projectsError: null,

  // Flow control
  requiresOrganizationSelection: false,
  requiresProjectSelection: false,

  // Auth transition loader state (for optimized login flow)
  authTransitionLoading: false,
  authTransitionMessage: null,
});

const getInitialRolePermissionsState = (): RuntimeRolePermissionsState => ({
  data: null,
  permissions: [],
  byKey: {},
  projectId: null,
  agentId: null,
  roleId: null,
  cin: null,
  superAdmin: false,
  hasAllPermissions: false,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
});

const initialState: AuthState = getInitialAuthState();

/**
 * Helper to reset organization and project state
 */
const resetOrgAndProjectState = (state: AuthState): void => {
  state.pendingAuth = null;
  state.organizations = [];
  state.selectedOrganization = null;
  state.organizationsLoading = false;
  state.organizationsError = null;
  state.projects = [];
  state.selectedProject = null;
  state.projectsLoading = false;
  state.projectsError = null;
  state.requiresOrganizationSelection = false;
  state.requiresProjectSelection = false;
  // Clear project tokens and claims
  state.projectAccessToken = null;
  state.projectRefreshToken = null;
  state.projectTokenClaims = null;
  state.rolePermissions = getInitialRolePermissionsState();
  state.currentSubscription = null;
  // Clear auth transition loader
  state.authTransitionLoading = false;
  state.authTransitionMessage = null;
};

/**
 * Helper to reset core auth state
 */
const resetCoreAuthState = (state: AuthState): void => {
  state.isAuthenticated = false;
  state.loginResponse = null;
  state.accessToken = null;
  state.wbaDetails = null;
  state.selectedWBAPhoneNumber = null;
  state.projectAccessToken = null;
  state.projectRefreshToken = null;
  state.projectTokenClaims = null;
  state.rolePermissions = getInitialRolePermissionsState();
  state.currentSubscription = null;
};

const mapRuntimePermissionsByKey = (
  permissions: string[],
  hasAllPermissions: boolean,
): Record<string, boolean> => {
  const byKey: Record<string, boolean> = {
    __runtime_permissions_loaded__: true,
  };
  permissions.forEach((permissionKey) => {
    if (typeof permissionKey === "string" && permissionKey.trim()) {
      byKey[permissionKey] = true;
    }
  });
  if (hasAllPermissions) {
    byKey["*"] = true;
  }
  return byKey;
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setEmailRefId: (state, action: PayloadAction<string>) => {
      state.emailRefId = action.payload;
    },
    setPhoneRefId: (state, action: PayloadAction<string>) => {
      state.phoneRefId = action.payload;
    },
    clearRefIds: (state) => {
      state.emailRefId = null;
      state.phoneRefId = null;
    },

    setLoginData: (state, action: PayloadAction<LoginResponse>) => {
      state.isAuthenticated = true;
      state.loginResponse = action.payload;
    },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    setRefreshToken: (state, action: PayloadAction<string>) => {
      if (state.loginResponse) {
        state.loginResponse.refreshToken = action.payload;
      }
      if (state.pendingAuth) {
        state.pendingAuth.refreshToken = action.payload;
      }
    },
    setWBADetails: (state, action: PayloadAction<WBADetailsResponse>) => {
      state.wbaDetails = action.payload;
      // Auto-select the first phone number if available and none selected
      if (action.payload.wba_phone_numbers?.length > 0 && !state.selectedWBAPhoneNumber) {
        state.selectedWBAPhoneNumber = action.payload.wba_phone_numbers[0];
      }
    },
    setSelectedWBAPhoneNumber: (state, action: PayloadAction<WBAPhoneNumber>) => {
      state.selectedWBAPhoneNumber = action.payload;
    },
    clearWBADetails: (state) => {
      state.wbaDetails = null;
      state.selectedWBAPhoneNumber = null;
    },
    /**
     * Standard logout - preserves emailRefId, phoneRefId, resetEmail
     */
    logout: (state) => {
      resetCoreAuthState(state);
      resetOrgAndProjectState(state);
    },

    /**
     * Force logout - clears everything in Redux state.
     * Storage cleanup is handled by the caller (useLogout, base-query)
     * via LogoutService.performFullCleanup().
     */
    forceLogout: (state) => {
      resetCoreAuthState(state);
      resetOrgAndProjectState(state);
      state.emailRefId = null;
      state.phoneRefId = null;
    },

    // ============================================
    // Pending Auth Reducers (New Login Flow)
    // ============================================

    /**
     * Store pending auth data from verify-login response.
     * Only stores refreshToken, email, and message - NOT loginUserDetails.
     * loginUserDetails will be constructed after org/project selection.
     */
    setPendingAuth: (state, action: PayloadAction<PendingAuthData>) => {
      state.pendingAuth = action.payload;
      state.requiresOrganizationSelection = true;
    },

    /**
     * Clear pending auth data (on logout or error).
     */
    clearPendingAuth: (state) => {
      state.pendingAuth = null;
    },

    /**
     * Construct loginUserDetails from selected organization and project.
     * Called after both organization and project have been selected.
     *
     * This is the final step in the auth flow that marks the user as authenticated.
     * Handles edge cases:
     * - Missing pendingAuth gracefully (uses existing loginResponse if available)
     * - Missing selectedOrganization (returns early without breaking state)
     * - Sanitizes input values to prevent undefined/null in the constructed object
     */
    constructLoginUserDetails: (
      state,
      action: PayloadAction<{
        firstName?: string;
        lastName?: string;
        role?: string;
        contactNo?: string;
        status?: string;
        isAgent?: boolean;
        profilePicUrl?: string | null;
      }>
    ) => {
      // Edge case: If already authenticated with valid loginResponse, update org/project refs only
      if (state.isAuthenticated && state.loginResponse?.loginUserDetails) {
        // Update the selected org/project in loginUserDetails
        if (state.selectedOrganization) {
          state.loginResponse.loginUserDetails.selectedOrganization = state.selectedOrganization;
        }
        if (state.selectedProject) {
          state.loginResponse.loginUserDetails.selectedProject = state.selectedProject;
        }
        state.requiresOrganizationSelection = false;
        state.requiresProjectSelection = false;
        return;
      }

      // Edge case: Must have selectedOrganization to construct loginUserDetails
      if (!state.selectedOrganization) {
        console.warn("[Auth] constructLoginUserDetails called without selectedOrganization");
        return;
      }

      // Edge case: Must have pendingAuth or existing loginResponse
      if (!state.pendingAuth && !state.loginResponse) {
        console.warn("[Auth] constructLoginUserDetails called without pendingAuth or loginResponse");
        return;
      }

      const org = state.selectedOrganization;
      const payload = action.payload;

      // Get email and refreshToken from pendingAuth or existing loginResponse
      const email = state.pendingAuth?.email ??
                    state.loginResponse?.loginUserDetails?.emailId ?? "";
      const refreshToken = state.pendingAuth?.refreshToken ??
                          state.loginResponse?.refreshToken ?? "";

      // Sanitize and construct firstName/lastName
      const firstName = (payload.firstName ?? "").trim();
      const lastName = (payload.lastName ?? "").trim();
      const regName = firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || "";

      // Construct loginUserDetails with sanitized values and selected org/project
      const loginUserDetails: LoginUserDetails = {
        id: org.agentId,
        firstName,
        lastName,
        regName,
        emailId: email,
        role: payload.role ?? "User",
        contactNo: payload.contactNo ?? "",
        ciNo: org.cin,
        brandName: org.businessName ?? "",
        status: payload.status ?? "ACTIVE",
        isAgent: payload.isAgent ?? true,
        profilePicUrl: payload.profilePicUrl ?? "",
        subscription: [],
        // Store selected organization and project in loginUserDetails
        selectedOrganization: state.selectedOrganization,
        selectedProject: state.selectedProject,
      };

      // Create the login response (avoid creating new object if values are same)
      state.loginResponse = {
        status: true,
        message: "Session constructed successfully",
        loginUserDetails,
        refreshToken,
      };

      // Mark as authenticated
      state.isAuthenticated = true;

      // Clear pending auth as it's now been used
      state.pendingAuth = null;

      // Clear flow control flags
      state.requiresOrganizationSelection = false;
      state.requiresProjectSelection = false;
    },

    // ============================================
    // Organization Reducers
    // ============================================

    setOrganizationsLoading: (state, action: PayloadAction<boolean>) => {
      state.organizationsLoading = action.payload;
      if (action.payload) {
        state.organizationsError = null;
      }
    },

    setOrganizations: (state, action: PayloadAction<UserBusiness[]>) => {
      state.organizations = action.payload;
      state.organizationsLoading = false;
      state.organizationsError = null;
      // Auto-select if only one organization
      if (action.payload.length === 1) {
        const org = action.payload[0];
        const selectedOrg: SelectedOrganization = {
          cin: org.cin,
          businessName: org.businessName,
          agentId: org.agentId,
          business_email: org.business_email,
          joinedAt: org.joinedAt,
          agentStatus: org.agentStatus,
        };
        state.selectedOrganization = selectedOrg;
        state.requiresOrganizationSelection = false;
        state.requiresProjectSelection = true;
        // Sync with loginUserDetails if authenticated
        if (state.loginResponse?.loginUserDetails) {
          state.loginResponse.loginUserDetails.selectedOrganization = selectedOrg;
          state.loginResponse.loginUserDetails.selectedProject = null;
          state.loginResponse.loginUserDetails.id = selectedOrg.agentId;
          state.loginResponse.loginUserDetails.ciNo = selectedOrg.cin;
          state.loginResponse.loginUserDetails.brandName = selectedOrg.businessName ?? "";
        }
      } else if (action.payload.length > 1) {
        state.requiresOrganizationSelection = true;
      } else {
        state.requiresOrganizationSelection = false;
      }
    },

    setOrganizationsError: (state, action: PayloadAction<string>) => {
      state.organizationsError = action.payload;
      state.organizationsLoading = false;
    },

    selectOrganization: (state, action: PayloadAction<SelectedOrganization>) => {
      state.selectedOrganization = action.payload;
      state.requiresOrganizationSelection = false;
      state.requiresProjectSelection = true;
      // Clear projects when organization changes
      state.projects = [];
      state.selectedProject = null;
      state.projectsError = null;
      // Clear project tokens and claims when organization changes
      state.projectAccessToken = null;
      state.projectRefreshToken = null;
      state.projectTokenClaims = null;
      state.rolePermissions = getInitialRolePermissionsState();
      state.currentSubscription = null;
      // Set loading to prevent "No Project Access" flash during org switch
      state.projectsLoading = true;
      // Sync with loginUserDetails if authenticated
      if (state.loginResponse?.loginUserDetails) {
        state.loginResponse.loginUserDetails.selectedOrganization = action.payload;
        state.loginResponse.loginUserDetails.selectedProject = null;
        // Update org-specific fields in loginUserDetails
        state.loginResponse.loginUserDetails.id = action.payload.agentId;
        state.loginResponse.loginUserDetails.ciNo = action.payload.cin;
        state.loginResponse.loginUserDetails.brandName = action.payload.businessName ?? "";
      }
    },

    clearSelectedOrganization: (state) => {
      state.selectedOrganization = null;
      state.requiresOrganizationSelection = state.organizations.length > 1;
      // Clear projects
      state.projects = [];
      state.selectedProject = null;
      state.projectsError = null;
      state.requiresProjectSelection = false;
      state.rolePermissions = getInitialRolePermissionsState();
      state.currentSubscription = null;
    },

    clearOrganizations: (state) => {
      state.organizations = [];
      state.selectedOrganization = null;
      state.organizationsLoading = false;
      state.organizationsError = null;
      state.requiresOrganizationSelection = false;
    },

    // ============================================
    // Project Reducers
    // ============================================

    setProjectsLoading: (state, action: PayloadAction<boolean>) => {
      state.projectsLoading = action.payload;
      if (action.payload) {
        state.projectsError = null;
      }
    },

    setProjects: (state, action: PayloadAction<OrganizationProject[]>) => {
      state.projects = action.payload;
      state.projectsLoading = false;
      state.projectsError = null;

      const hasSelectedProjectInList =
        state.selectedProject !== null &&
        action.payload.some(
          (project) => project.projectId === state.selectedProject?.projectId,
        );

      if (state.selectedProject && !hasSelectedProjectInList) {
        state.selectedProject = null;
        state.projectAccessToken = null;
        state.projectRefreshToken = null;
        state.projectTokenClaims = null;
        state.rolePermissions = getInitialRolePermissionsState();
        if (state.loginResponse?.loginUserDetails) {
          state.loginResponse.loginUserDetails.selectedProject = null;
        }
      }

      if (action.payload.length > 0) {
        state.requiresProjectSelection = state.selectedProject === null;
      } else {
        state.requiresProjectSelection = false;
      }
    },

    setProjectsError: (state, action: PayloadAction<string>) => {
      state.projectsError = action.payload;
      state.projectsLoading = false;
    },

    selectProject: (state, action: PayloadAction<SelectedProject>) => {
      const previousProjectId = state.selectedProject?.projectId ?? null;
      const nextProjectId = action.payload.projectId ?? null;
      state.selectedProject = action.payload;
      state.requiresProjectSelection = false;
      if (previousProjectId !== nextProjectId) {
        state.projectAccessToken = null;
        state.projectRefreshToken = null;
        state.projectTokenClaims = null;
        state.rolePermissions = getInitialRolePermissionsState();
      }
      // Sync with loginUserDetails if authenticated
      if (state.loginResponse?.loginUserDetails) {
        state.loginResponse.loginUserDetails.selectedProject = action.payload;
      }
    },

    clearSelectedProject: (state) => {
      state.selectedProject = null;
      state.requiresProjectSelection = state.projects.length > 1;
      state.projectAccessToken = null;
      state.projectRefreshToken = null;
      state.projectTokenClaims = null;
      state.rolePermissions = getInitialRolePermissionsState();
      state.currentSubscription = null;
      if (state.loginResponse?.loginUserDetails) {
        state.loginResponse.loginUserDetails.selectedProject = null;
      }
    },

    clearProjects: (state) => {
      state.projects = [];
      state.selectedProject = null;
      state.projectsLoading = false;
      state.projectsError = null;
      state.requiresProjectSelection = false;
      state.rolePermissions = getInitialRolePermissionsState();
      state.currentSubscription = null;
    },

    // ============================================
    // Flow Control Reducers
    // ============================================

    setRequiresOrganizationSelection: (state, action: PayloadAction<boolean>) => {
      state.requiresOrganizationSelection = action.payload;
    },

    setRequiresProjectSelection: (state, action: PayloadAction<boolean>) => {
      state.requiresProjectSelection = action.payload;
    },

    // Complete session setup after org/project selection
    completeSessionSetup: (state) => {
      state.requiresOrganizationSelection = false;
      state.requiresProjectSelection = false;
    },

    // ============================================
    // Project Token Reducers
    // ============================================

    setProjectAccessToken: (state, action: PayloadAction<string>) => {
      state.projectAccessToken = action.payload;
    },

    setProjectRefreshToken: (state, action: PayloadAction<string>) => {
      state.projectRefreshToken = action.payload;
    },

    /**
     * Store decoded project token claims.
     * Called alongside setProjectAccessToken — decoding happens outside
     * the reducer (in base-query / orchestration hook) to keep reducers pure.
     */
    setProjectTokenClaims: (state, action: PayloadAction<ProjectTokenClaims | null>) => {
      state.projectTokenClaims = action.payload;
    },

    clearProjectTokens: (state) => {
      state.projectAccessToken = null;
      state.projectRefreshToken = null;
      state.projectTokenClaims = null;
      state.rolePermissions = getInitialRolePermissionsState();
      state.currentSubscription = null;
    },

    setRolePermissionsLoading: (
      state,
      action: PayloadAction<{ projectId: number | null; cin: string | null }>,
    ) => {
      state.rolePermissions.isLoading = true;
      state.rolePermissions.error = null;
      state.rolePermissions.projectId = action.payload.projectId;
      state.rolePermissions.cin = action.payload.cin;
    },

    setRolePermissions: (
      state,
      action: PayloadAction<RuntimeRolePermissionsData>,
    ) => {
      const permissions = Array.isArray(action.payload.permissions)
        ? action.payload.permissions
        : [];
      const hasAllPermissions =
        action.payload.hasAllPermissions === true ||
        action.payload.superAdmin === true;
      state.rolePermissions = {
        data: action.payload,
        permissions,
        byKey: mapRuntimePermissionsByKey(permissions, hasAllPermissions),
        projectId: action.payload.projectId ?? null,
        agentId: action.payload.agentId ?? null,
        roleId: action.payload.roleId,
        cin: action.payload.cin,
        superAdmin: action.payload.superAdmin === true,
        hasAllPermissions,
        isLoading: false,
        error: null,
        lastFetchedAt: Date.now(),
      };
    },

    setRolePermissionsError: (
      state,
      action: PayloadAction<{
        error: string;
        projectId: number | null;
        cin: string | null;
      }>,
    ) => {
      state.rolePermissions.isLoading = false;
      state.rolePermissions.error = action.payload.error;
      state.rolePermissions.projectId = action.payload.projectId;
      state.rolePermissions.cin = action.payload.cin;
      state.rolePermissions.lastFetchedAt = Date.now();
    },

    clearRolePermissions: (state) => {
      state.rolePermissions = getInitialRolePermissionsState();
    },

    setAuthCurrentSubscription: (
      state,
      action: PayloadAction<CurrentSubscriptionData | null>,
    ) => {
      state.currentSubscription = action.payload;
    },

    // ============================================
    // Auth Transition Loader Reducers (Optimized Login Flow)
    // ============================================

    /**
     * Set the auth transition loading state with optional message.
     * Used for full-screen loaders during auth flow transitions.
     */
    setAuthTransitionLoading: (
      state,
      action: PayloadAction<{ loading: boolean; message?: string | null }>
    ) => {
      state.authTransitionLoading = action.payload.loading;
      state.authTransitionMessage = action.payload.message ?? null;
    },

    /**
     * Update loginUserDetails with data extracted from JWT token.
     * Called when access token is refreshed to keep user data in sync.
     * Only updates fields that have non-empty values in the token.
     */
    updateUserDetailsFromToken: (
      state,
      action: PayloadAction<{
        firstName?: string;
        lastName?: string;
        email?: string;
        role?: string;
        isAgent?: boolean;
        status?: string;
        ciNo?: string;
        profilePicUrl?: string;
      }>
    ) => {
      if (!state.loginResponse?.loginUserDetails) {
        return;
      }

      const payload = action.payload;
      const details = state.loginResponse.loginUserDetails;

      // Only update non-empty values from token
      if (payload.firstName && payload.firstName.trim()) {
        details.firstName = payload.firstName.trim();
      }
      if (payload.lastName && payload.lastName.trim()) {
        details.lastName = payload.lastName.trim();
      }
      if (payload.email && payload.email.trim()) {
        details.emailId = payload.email.trim();
      }
      if (payload.role) {
        details.role = payload.role;
      }
      if (typeof payload.isAgent === "boolean") {
        details.isAgent = payload.isAgent;
      }
      if (payload.status) {
        details.status = payload.status;
      }
      if (payload.ciNo) {
        details.ciNo = payload.ciNo;
      }
      if (payload.profilePicUrl) {
        details.profilePicUrl = payload.profilePicUrl;
      }

      // Update regName if firstName or lastName changed
      if (details.firstName || details.lastName) {
        details.regName = `${details.firstName || ""} ${details.lastName || ""}`.trim();
      }
    },
  },
});

export const {
  // Existing actions
  setEmailRefId,
  setPhoneRefId,
  clearRefIds,

  setLoginData,
  setAccessToken,
  setRefreshToken,
  setWBADetails,
  setSelectedWBAPhoneNumber,
  clearWBADetails,
  logout,
  forceLogout,

  // Pending auth actions (new flow)
  setPendingAuth,
  clearPendingAuth,
  constructLoginUserDetails,

  // Organization actions
  setOrganizationsLoading,
  setOrganizations,
  setOrganizationsError,
  selectOrganization,
  clearSelectedOrganization,
  clearOrganizations,

  // Project actions
  setProjectsLoading,
  setProjects,
  setProjectsError,
  selectProject,
  clearSelectedProject,
  clearProjects,

  // Flow control actions
  setRequiresOrganizationSelection,
  setRequiresProjectSelection,
  completeSessionSetup,

  // Project token actions
  setProjectAccessToken,
  setProjectRefreshToken,
  setProjectTokenClaims,
  clearProjectTokens,

  // Runtime role permissions
  setRolePermissionsLoading,
  setRolePermissions,
  setRolePermissionsError,
  clearRolePermissions,
  setAuthCurrentSubscription,

  // Auth transition loader actions (optimized login flow)
  setAuthTransitionLoading,

  // Token-based user details update
  updateUserDetailsFromToken,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
export default authReducer;
